import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Edit2, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useB2BAuth } from '../../context/B2BAuthContext';
import { useB2BOrders } from '../../context/B2BOrderContext';

const B2BOrderConfirm = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { user, isAuthenticated } = useB2BAuth();
  const { orders, confirmOrder, updateOrder } = useB2BOrders();
  const [editMode, setEditMode] = useState(false);
  const [editedItems, setEditedItems] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/b2b/login');
    }
  }, [isAuthenticated, navigate]);

  const order = orders.find(o => o.id === orderId);

  useEffect(() => {
    if (order) {
      const items = {};
      order.items.forEach(item => {
        items[item.productId] = item.quantity;
      });
      setEditedItems(items);
    }
  }, [order]);

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p>الطلب غير موجود</p>
      </div>
    );
  }

  const handleQuantityChange = (productId, newQuantity) => {
    setEditedItems(prev => ({
      ...prev,
      [productId]: Math.max(0, parseInt(newQuantity) || 0)
    }));
  };

  const saveChanges = () => {
    const changes = [];
    const newItems = order.items.map(item => {
      const newQty = editedItems[item.productId];
      if (newQty !== item.quantity) {
        changes.push(`${item.productName}: ${item.quantity} → ${newQty}`);
      }
      return {
        ...item,
        previousQuantity: item.quantity,
        quantity: newQty,
        totalPrice: item.unitPrice * newQty
      };
    }).filter(item => item.quantity > 0);

    const newTotal = newItems.reduce((sum, item) => sum + item.totalPrice, 0);

    updateOrder(orderId, {
      items: newItems,
      totalAmount: newTotal
    }, `تعديل: ${changes.join('، ')}`);

    setEditMode(false);
  };

  const handleConfirm = async () => {
    setConfirming(true);
    
    // Play success sound
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZUBE');
    audio.play().catch(() => {}); // Ignore if audio fails
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    confirmOrder(orderId);
    setConfirmed(true);
    setConfirming(false);
  };

  const currentTotal = order.items.reduce((sum, item) => {
    const qty = editMode ? (editedItems[item.productId] || 0) : item.quantity;
    return sum + item.unitPrice * qty;
  }, 0);

  if (confirmed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(to bottom, #f5f5f5, #ffffff)' }}>
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md w-full border-2" style={{ borderColor: '#2d7a2d' }}>
          <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce" style={{ background: '#2d7a2d' }}>
            <Check className="w-12 h-12 text-white" strokeWidth={3} />
          </div>
          <h1 className="text-3xl font-bold mb-3" style={{ color: '#1a5c1a' }}>تم تأكيد الطلبية بنجاح! ✓</h1>
          <div className="my-6 p-4 rounded-xl" style={{ background: '#f0f9f0' }}>
            <p className="text-sm text-gray-600 mb-2">رقم الطلب</p>
            <p className="text-3xl font-bold" style={{ color: '#2d7a2d' }}>{order.orderNumber}</p>
          </div>
          <div className="p-4 rounded-xl mb-6" style={{ background: '#fff8e1', border: '2px solid #ffd54f' }}>
            <p className="font-semibold" style={{ color: '#f57c00' }}>
              🔔 الطلبية وصلت لإدارة المخبز
            </p>
          </div>
          <Button
            onClick={() => navigate('/b2b/dashboard')}
            className="w-full text-white py-6 text-lg font-bold"
            style={{ background: '#2d7a2d' }}
          >
            العودة للرئيسية
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="font-bold text-gray-900">مراجعة الطلب</h1>
                <p className="text-sm text-gray-500">{order.orderNumber}</p>
              </div>
            </div>
            {!editMode && order.status === 'pending' && (
              <button
                onClick={() => setEditMode(true)}
                className="flex items-center gap-2 text-green-600 hover:text-green-700"
              >
                <Edit2 className="w-4 h-4" />
                <span>تعديل</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Order Info */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-4">
          <div className="flex items-center gap-3 text-gray-500">
            <Clock className="w-5 h-5" />
            <span>{new Date(order.createdAt).toLocaleString('ar-DE')}</span>
          </div>
        </div>

        {/* Order Items */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-bold text-gray-900">تفاصيل الطلب</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {order.items.map((item) => {
              const currentQty = editMode ? (editedItems[item.productId] || 0) : item.quantity;
              const hasChanged = item.previousQuantity !== undefined && item.previousQuantity !== item.quantity;

              return (
                <div key={item.productId} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{item.productName}</h3>
                      <p className="text-sm text-gray-500">€{item.unitPrice.toFixed(2)} / {item.unit}</p>
                    </div>
                    <div className="text-left">
                      {editMode ? (
                        <input
                          type="number"
                          value={currentQty}
                          onChange={(e) => handleQuantityChange(item.productId, e.target.value)}
                          className="w-20 text-center py-2 border border-gray-200 rounded-lg font-bold"
                          min="0"
                        />
                      ) : (
                        <div>
                          {hasChanged && (
                            <span className="text-gray-400 line-through text-sm block">
                              {item.previousQuantity} {item.unit}
                            </span>
                          )}
                          <span className="font-bold text-gray-900">
                            {item.quantity} {item.unit}
                          </span>
                        </div>
                      )}
                      <p className="text-green-600 font-bold mt-1">
                        €{(item.unitPrice * currentQty).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="p-4 bg-gray-50 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <span className="font-bold text-gray-900">الإجمالي</span>
              <span className="text-2xl font-bold text-green-600">€{currentTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Order History */}
        {order.history.length > 1 && (
          <div className="mt-4 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">سجل التعديلات</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {order.history.map((entry, index) => (
                <div key={index} className="p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">
                      {new Date(entry.timestamp).toLocaleTimeString('ar-DE')}
                    </span>
                    <span className="text-gray-700">{entry.details}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          {editMode ? (
            <div className="flex gap-3">
              <Button
                onClick={() => setEditMode(false)}
                variant="outline"
                className="flex-1 py-6"
              >
                إلغاء
              </Button>
              <Button
                onClick={saveChanges}
                className="flex-1 bg-green-600 hover:bg-green-700 py-6"
              >
                حفظ التعديلات
              </Button>
            </div>
          ) : (
            <Button
              onClick={handleConfirm}
              disabled={confirming}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold rounded-xl"
            >
              {confirming ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  جاري التأكيد...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Check className="w-5 h-5" />
                  تأكيد الطلبية نهائياً
                </span>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default B2BOrderConfirm;